/**
 * Contexte d'authentification React.
 * Gère l'état de l'utilisateur connecté, le token JWT, et les fonctions login/logout.
 */

import React, { createContext, useContext, useState } from "react";
import { login as apiLogin } from "../api/authApi";

const AuthContext = createContext(null);

/**
 * Hook personnalisé pour accéder au contexte d'authentification.
 * @returns {Object} { user, token, loginUser, logoutUser }
 */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Provider du contexte d'authentification.
 * Encapsule l'application pour fournir l'état auth à tous les composants.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem("access_token") || null;
  });

  /**
   * Connecte un utilisateur via l'API.
   * @param {string} email - Adresse email.
   * @param {string} password - Mot de passe.
   */
  async function loginUser(email, password) {
    const data = await apiLogin(email, password);
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }

  /** Déconnecte l'utilisateur et nettoie le stockage local. */
  function logoutUser() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  }

  const value = { user, token, loginUser, logoutUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
